"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { Check, ClipboardCopy, Store, X } from "lucide-react";
import type { NormalizedStore } from "@/@types/campaign";

interface StoreExcelUploadPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (matchedCodes: number[]) => void;
  matchedStores: NormalizedStore[];
  alreadySelected: number[];
  notFound: string[];
}

export function StoreExcelUploadPreview({
  isOpen,
  onClose,
  onConfirm,
  matchedStores,
  alreadySelected,
  notFound,
}: StoreExcelUploadPreviewProps) {
  const alreadySet = new Set(alreadySelected);
  const newStores = matchedStores.filter(store => !alreadySet.has(store.code));

  const handleConfirm = () => {
    if (matchedStores.length === 0) return;
    onConfirm(matchedStores.map(store => store.code));
    onClose();
  };

  const handleCopyNotFound = async () => {
    try {
      await navigator.clipboard.writeText(notFound.join("\n"));
      toast({
        title: "Lista copiada",
        description: `${notFound.length} valor(es) não encontrado(s) copiado(s).`,
        variant: "default",
      });
    } catch {
      toast({
        title: "Não foi possível copiar",
        description: "Copie a lista manualmente.",
        variant: "destructive",
      });
    }
  };

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[60] bg-black/80",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-[60] translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] max-w-2xl max-h-[85vh]",
            "grid gap-4 border bg-white p-6 shadow-lg duration-200 sm:rounded-lg",
            "overflow-hidden flex flex-col",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
        >
          <div className="flex-shrink-0 flex flex-col space-y-1.5 text-left">
            <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
              Pré-visualização da Importação de Lojas
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-muted-foreground">
              {matchedStores.length} loja(s) encontrada(s) no arquivo Excel
            </DialogPrimitive.Description>
          </div>

          <div className="flex-1 overflow-y-auto space-y-5 pr-1 min-h-0">
            <div className="grid grid-cols-3 gap-3">
              <div className="border rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium">Novas</span>
                </div>
                <span className="text-2xl font-bold">{newStores.length}</span>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Store className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium">Já marcadas</span>
                </div>
                <span className="text-2xl font-bold">{alreadySelected.length}</span>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <X className="h-4 w-4 text-destructive" />
                  <span className="text-sm font-medium">Não encontradas</span>
                </div>
                <span className="text-2xl font-bold">{notFound.length}</span>
              </div>
            </div>

            {matchedStores.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Lojas a selecionar ({matchedStores.length})
                </p>
                <div className="border rounded-lg max-h-[220px]">
                  <ScrollArea className="max-h-[220px]">
                    <div className="space-y-0.5 p-2">
                      {matchedStores.slice(0, 100).map(store => (
                        <div
                          key={store.code}
                          className="flex items-center gap-2 text-sm py-1.5 px-2 rounded hover:bg-muted/50"
                        >
                          <Check className="h-3 w-3 text-green-600 flex-shrink-0" />
                          <span className="font-mono font-medium">{store.code}</span>
                          <span className="text-muted-foreground truncate">
                            — {store.name}
                          </span>
                          {alreadySet.has(store.code) && (
                            <Badge variant="outline" className="ml-auto text-[10px]">
                              já marcada
                            </Badge>
                          )}
                        </div>
                      ))}
                      {matchedStores.length > 100 && (
                        <p className="text-xs text-muted-foreground text-center py-2">
                          ...e mais {matchedStores.length - 100} loja(s)
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            )}

            {notFound.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    Não encontradas no sistema ({notFound.length})
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyNotFound}
                    className="h-7 gap-1 text-muted-foreground"
                  >
                    <ClipboardCopy className="h-3.5 w-3.5" />
                    Copiar lista
                  </Button>
                </div>
                <div className="border rounded-lg max-h-[120px] overflow-y-auto">
                  <div className="flex flex-wrap gap-1.5 p-2">
                    {notFound.map(value => (
                      <Badge
                        key={value}
                        variant="destructive"
                        className="font-mono text-xs rounded-md whitespace-normal break-all text-left"
                      >
                        {value}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-4 border-t flex-shrink-0">
            <Button variant="outline" onClick={onClose} className="w-full md:w-auto">
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={matchedStores.length === 0}
              className="w-full md:w-auto"
            >
              Selecionar {matchedStores.length} loja(s)
            </Button>
          </div>

          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
